import { Citation, paperMageBoxToBoundingBox } from '@/models/Citations';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { InlineCitations } from '@/components/reader/InlineCitations';
import { Outline } from '@/components/reader/Outline';
import { READER_BACKEND_HOST } from '@/api/ChatApi';
import { ScrollToOnChange } from '@/components/reader/ScrollToOnChange';
import { Thumbnail } from '@/components/reader/Thumbnail';

import { BoundingBox } from '@allenai/pdf-components/src/components/types/boundingBox'; // eslint-disable-line import/no-unresolved
import {
  createRef,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  DocumentContext,
  DocumentWrapper,
  Overlay,
  PageWrapper,
  RENDER_TYPE,
} from '@allenai/pdf-components';

interface PropType {
  pdfUrl: string;
  highlights?: BoundingBox[];
  corpusId: number;
}

export const Document: FunctionComponent<PropType> = (props) => {
  const { pdfUrl, highlights = [], corpusId } = props;

  // ref for the div in which the Document component renders
  const pdfContentRef = createRef<HTMLDivElement>();

  // ref for the scrollable region where the pages are rendered
  const pdfScrollableRef = createRef<HTMLDivElement>();

  const { numPages, pageDimensions } = useContext(DocumentContext);

  const [citations, setCitations] = useState<Citation[]>();
  useEffect(() => {
    if (
      !pageDimensions ||
      !corpusId ||
      pageDimensions.height === 0 ||
      pageDimensions.width === 0
    ) {
      return;
    }
    const url = `${READER_BACKEND_HOST}/document/${corpusId}`;

    setCitations([]);
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const newCitations: Citation[] = [];
        const rawCitations = data?.entities?.citations ?? [];
        rawCitations.forEach((citation: any) => {
          newCitations.push({
            citedPaperId: citation.metadata.corpusId,
            boundingBoxes: citation.boxes.map((box: number[]) =>
              paperMageBoxToBoundingBox(box, pageDimensions),
            ),
            metadata: citation.metadata,
          });
        });
        setCitations(newCitations);
      })
      .catch((error: any) => {
        console.error('Error fetching citations:', error);
        getLayoverLogger().logError('Error fetching citations', error);
        setCitations([]);
      });
  }, [corpusId, pageDimensions]);

  return (
    <DocumentWrapper
      key={pdfUrl}
      className="reader__main"
      file={pdfUrl}
      inputRef={pdfContentRef}
      renderType={RENDER_TYPE.MULTI_CANVAS}
    >
      <Outline parentRef={pdfContentRef} />
      <Thumbnail parentRef={pdfContentRef} />
      <div className="reader__page-list" ref={pdfScrollableRef}>
        {Array.from({ length: numPages }).map((_, i) => (
          <PageWrapper
            key={`${pdfUrl}_${i}`}
            pageIndex={i}
            renderType={RENDER_TYPE.SINGLE_CANVAS}
          >
            <Overlay>
              <ScrollToOnChange
                pageIndex={i}
                boxes={highlights}
                offsetY={-200}
              />
              <InlineCitations
                citations={citations}
                pageIndex={i}
                parentRef={pdfScrollableRef}
              />
            </Overlay>
          </PageWrapper>
        ))}
      </div>
    </DocumentWrapper>
  );
};

export default Document;
